import { useBackendApi } from './backendApi';
import { useEventsPublisher } from 'event-bus';
import { Plan, PLAN_PROJECT_TYPE_UPDATED, PLAN_UPDATED } from './plansService';
import { ValidationSchema } from 'validation-schema';
import { ProjectType } from './Types';

export const PLANS_ADMIN_UPDATED = 'PLANS_ADMIN_UPDATED';

export type CopyPlanAsGlobalRequest = {
  name: string;
  setupAreaId: number;
  setupAreaVersion: string;
};

export function usePlansAdminService() {
  const nekstApi = useBackendApi();
  const eventsPublisher = useEventsPublisher();

  const createPlan = async (
    areaId: number,
    version: string,
    data: any
  ): Promise<Plan> => {
    const result = await nekstApi.post(`/manager/setupareas/${areaId}/${version}/plans`, data) as Plan;
    eventsPublisher.publish(PLANS_ADMIN_UPDATED, {});
    return result;
  };

  const updatePlan = async (
    planId: number,
    data: any
  ): Promise<Plan> => {
    const result = await nekstApi.put(`/manager/plans/${planId}`, data) as Plan;
    eventsPublisher.publish(PLANS_ADMIN_UPDATED, {});
    return result;
  };

  const deletePlan = async (
    planId: number
  ): Promise<void> => {
    await nekstApi.delete(`/manager/plans/${planId}`);
    eventsPublisher.publish(PLANS_ADMIN_UPDATED, {});
  };

  const getPlans = async (
    areaId: number,
    version: string
  ): Promise<Plan[]> => {
    return await nekstApi.get(`/manager/setupareas/${areaId}/${version}/plans`) as Plan[];
  };

  const copyPlanAsGlobal = async (
    planId: number,
    request: CopyPlanAsGlobalRequest,
  ): Promise<Plan> => {
    return await nekstApi.post(`/manager/plans/${planId}/copy`, request) as Plan;
  }

  const changePlanProjectType = async (
    planId: number,
    projectType: ProjectType
  ): Promise<Plan> => {
    const result = await nekstApi.put(`/manager/plans/${planId}/projecttype`, {
      projectType,
    })

    eventsPublisher.publish(PLAN_PROJECT_TYPE_UPDATED, {
      id: planId,
      projectType,
    });
    eventsPublisher.publish(PLAN_UPDATED, {
      id: planId,
      data: result,
    })

    return result;
  }

  const getAllGlobalPlans = async (): Promise<Plan[]> => {
    return await nekstApi.get('/manager/plans') as Plan[];
  }

  const getCreateValidationSchema = () => {
    return {
      name: {
        type: 'string',
        constraints: {
          required: true,
        },
      },
      projectType: {
        type: 'string',
        constraints: {
          required: true,
        },
      },
    } as ValidationSchema;
  };

  return {
    createPlan,
    updatePlan,
    deletePlan,
    getPlans,
    copyPlanAsGlobal,
    changeProjectType: changePlanProjectType,
    getCreateValidationSchema,
    getAllGlobalPlans,
  };
}
